import {
  linkedContextOriginAtom,
  linkedPatientContextAtom,
  linkedSessionContextAtom,
} from '@/atoms/chatAtoms';
import { isChatShareApiAvailable } from '@/utils/backendSurfaces';
import { useAtomValue } from 'jotai';
import { isEvidenceChatClinicallyLinked } from '../utils/isEvidenceChatClinicallyLinked';

interface UseCanShareEvidenceChatParams {
  chatId?: string | null;
  backendSessionId?: string | null;
  backendPatientProfileId?: string | null;
  isShareEnabled: boolean;
  /** EVI-2279 — when true, atom-staged mid-chat links also block share. */
  isMidChatPersistEnabled: boolean;
}

/**
 * Whether the standalone Evidence chat may expose the share action.
 * Share requires the share-chat flag, a chat id, no clinical link — and a
 * server that mounts the share routes: `POST /chat/chats/{id}/share` stays in
 * ml-scribe's cloud-only chat router, so on an on-prem build the action is
 * withheld however the flag resolves.
 */
export function useCanShareEvidenceChat({
  chatId,
  backendSessionId,
  backendPatientProfileId,
  isShareEnabled,
  isMidChatPersistEnabled,
}: UseCanShareEvidenceChatParams): boolean {
  const linkedSession = useAtomValue(linkedSessionContextAtom);
  const linkedPatient = useAtomValue(linkedPatientContextAtom);
  const contextOrigin = useAtomValue(linkedContextOriginAtom);

  if (!isChatShareApiAvailable || !isShareEnabled || !chatId) {
    return false;
  }

  const scopedLinkedSessionId =
    contextOrigin === chatId ? linkedSession?.id : undefined;
  const scopedLinkedPatientProfileId =
    contextOrigin === chatId ? linkedPatient?.id : undefined;

  return !isEvidenceChatClinicallyLinked({
    backendSessionId,
    backendPatientProfileId,
    linkedSessionId: scopedLinkedSessionId,
    linkedPatientProfileId: scopedLinkedPatientProfileId,
    includeOutgoingLinks: isMidChatPersistEnabled,
  });
}
